import React, { memo, useCallback, useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { rem } from 'design-system/values';
import { CardView } from 'design-system/CardView';
import { Indicator } from 'design-system/Indicator';
import Text from 'design-system/Text';
import { IconArrowTriangleLeft } from 'design-system/icons/IconArrowTriangleLeft';
import { IconArrowTriangleRight } from 'design-system/icons/IconArrowTriangleRight';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { buildCalendarWeeks, formatMonthTitle, isStreakDay, shiftDay, WEEK_DAYS, } from '../utils';
const DAY_SIZE = rem(34);
// Today is a flame that rises above the circle the other days sit in
const TODAY_FLAME_HEIGHT = DAY_SIZE * 0.97;
const TODAY_FLAME_PATH = 'M16.25.1c0-.08-.08-.12-.15-.09C9.4 3.61 8.81 11.08 8.75 12.25c0 .08-.08.12-.14.08-.43-.27-1.75-1.28-2.09-3.45-.01-.07-.09-.11-.15-.07C2.19 11.22 0 15.16 0 20.57 0 27.4 5.82 32.95 13 32.95S26 27.4 26 20.57C26 10.57 16.38 8.47 16.25.1Z';
const BAND_CONTINUES_RADIUS = rem(8);
const ARROW_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 };
// Text takes its colour from variant props; `style` cannot override it
const DAY_TEXT_COLOR = {
    active: { OnBrand: true },
    today: { White: true },
    inactive: { 'Gray-50': true },
    outside: { 'Gray-50': true },
};
const isHighlighted = (day) => day?.state === 'active' || day?.state === 'today';
const TodayFlame = () => {
    const colors = useColors();
    return (<Svg width={(TODAY_FLAME_HEIGHT * 26) / 32.95} height={TODAY_FLAME_HEIGHT} viewBox="0 0 26 32.95" style={staticStyles.todayFlame}>
      <Path d={TODAY_FLAME_PATH} fill={colors.elements.brand}/>
    </Svg>);
};
const CalendarWeek = ({ week, isStreakDay }) => {
    const styles = useStyles(styleSet);
    return (<View style={staticStyles.week}>
      {week.map((day, index) => {
            // Consecutive active days are joined into one band. Its ends are round
            // where the streak starts or stops, and only slightly rounded where it
            // carries on into the row or month before or after
            const isBand = day.state === 'active' || day.state === 'today';
            const joinsPrevious = isBand && isHighlighted(week[index - 1]);
            const joinsNext = day.state === 'active' && isHighlighted(week[index + 1]);
            const continuesBefore = isBand && !joinsPrevious && isStreakDay(shiftDay(day.date, -1));
            const continuesAfter = day.state === 'active' &&
                !joinsNext &&
                isStreakDay(shiftDay(day.date, 1));
            const isToday = day.state === 'today';
            // A day on its own is a circle rather than a band
            const isAlone = day.state === 'active' &&
                !joinsPrevious &&
                !joinsNext &&
                !continuesBefore &&
                !continuesAfter;
            const hasBand = (day.state === 'active' && !isAlone) ||
                (isToday && (joinsPrevious || continuesBefore));
            return (<View key={day.date} style={staticStyles.dayCell} testID={`streaks-day-${day.date}`} accessibilityLabel={`${day.date} ${day.state}`}>
            {hasBand ? (<View testID={`streaks-band-${day.date}`} style={[
                        styles.band,
                        joinsPrevious && staticStyles.bandJoinsPrevious,
                        continuesBefore && staticStyles.bandContinuesBefore,
                        joinsNext && staticStyles.bandJoinsNext,
                        continuesAfter && staticStyles.bandContinuesAfter,
                    ]}/>) : null}
            <View style={[staticStyles.day, isAlone && styles.alone]}>
              {isToday ? <TodayFlame /> : null}
              <Text Text-14 {...DAY_TEXT_COLOR[day.state]}>
                {day.dayOfMonth}
              </Text>
            </View>
          </View>);
        })}
    </View>);
};
const StreaksCalendar = ({ month, today, activeDays, isLoading, canGoBack, canGoForward, onPressPrevious, onPressNext, }) => {
    const colors = useColors();
    const activeDaySet = useMemo(() => new Set(activeDays), [activeDays]);
    const weeks = useMemo(() => buildCalendarWeeks({ month, today, activeDays: activeDaySet }), [month, today, activeDaySet]);
    const isDayInStreak = useCallback((day) => isStreakDay(day, { today, activeDays: activeDaySet }), [today, activeDaySet]);
    return (<CardView noMargin style={staticStyles.card}>
      <View style={staticStyles.header}>
        <TouchableOpacity style={staticStyles.arrow} onPress={onPressPrevious} disabled={!canGoBack} hitSlop={ARROW_HIT_SLOP} testID="streaks-calendar-previous" accessibilityLabel="Previous month">
          {canGoBack ? (<IconArrowTriangleLeft width={rem(10)} height={rem(9)} color={colors.elements.secondary}/>) : null}
        </TouchableOpacity>
        <Text Text-16>{formatMonthTitle(month)}</Text>
        <TouchableOpacity style={staticStyles.arrow} onPress={onPressNext} disabled={!canGoForward} hitSlop={ARROW_HIT_SLOP} testID="streaks-calendar-next" accessibilityLabel="Next month">
          {canGoForward ? (<IconArrowTriangleRight width={rem(10)} height={rem(9)} color={colors.elements.secondary}/>) : null}
        </TouchableOpacity>
      </View>
      <View style={staticStyles.weekDays}>
        {WEEK_DAYS.map((weekDay) => (<Text key={weekDay} Text-14 Secondary style={staticStyles.weekDay}>
            {weekDay}
          </Text>))}
      </View>
      <View>
        {/* Hidden rather than removed, so the card keeps its height */}
        <View style={[staticStyles.weeks, isLoading && staticStyles.hidden]} accessibilityElementsHidden={isLoading} importantForAccessibility={isLoading ? 'no-hide-descendants' : 'auto'}>
          {weeks.map((week) => (<CalendarWeek key={week[0].date} week={week} isStreakDay={isDayInStreak}/>))}
        </View>
        {isLoading ? (<View style={StyleSheet.absoluteFill} testID="streaks-calendar-loader">
            <Indicator dark/>
          </View>) : null}
      </View>
    </CardView>);
};
const styleSet = createStyleSheets((colors) => ({
    band: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: colors.background.brandLight,
        borderRadius: DAY_SIZE / 2,
    },
    alone: {
        backgroundColor: colors.background.brandLight,
    },
}));
const staticStyles = StyleSheet.create({
    card: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(16),
    },
    header: {
        height: rem(56),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    arrow: {
        width: rem(24),
        height: rem(24),
        alignItems: 'center',
        justifyContent: 'center',
    },
    weekDays: {
        flexDirection: 'row',
        paddingBottom: rem(16),
    },
    weekDay: {
        flex: 1,
        textAlign: 'center',
    },
    weeks: {
        gap: rem(16),
    },
    hidden: {
        opacity: 0,
    },
    week: {
        flexDirection: 'row',
    },
    dayCell: {
        flex: 1,
        height: DAY_SIZE,
        alignItems: 'center',
        justifyContent: 'center',
    },
    day: {
        width: DAY_SIZE,
        height: DAY_SIZE,
        borderRadius: DAY_SIZE / 2,
        alignItems: 'center',
        justifyContent: 'center',
    },
    bandJoinsPrevious: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    },
    bandJoinsNext: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },
    bandContinuesBefore: {
        borderTopLeftRadius: BAND_CONTINUES_RADIUS,
        borderBottomLeftRadius: BAND_CONTINUES_RADIUS,
    },
    bandContinuesAfter: {
        borderTopRightRadius: BAND_CONTINUES_RADIUS,
        borderBottomRightRadius: BAND_CONTINUES_RADIUS,
    },
    todayFlame: {
        position: 'absolute',
        top: -DAY_SIZE * 0.09,
        alignSelf: 'center',
    },
});
export default memo(StreaksCalendar);
